import {useCallback} from "react";
import { useDispatch } from "react-redux";
import {useAppSelector} from "hooks/useAppSelector";
import {setGTMInitialized} from "app/store/slices/setting";
import {
    IBonusClaimPayload,
    IBonusSuccessPayload,
    IDepositErrorPayload,
    IDepositInitPayload,
    IDepositSuccessPayload,
    IEventPayload,
    IFormErrorPayload,
    IFormEventPayload,
    IInitPayload,
    IRegistrationPayload,
} from "app/types/gtm";

export const useGTMEvents = () => {
    const {isGTMInitialized} = useAppSelector((state) => state.setting);
    const user = useAppSelector((state) => state.user.user);
    const dispatch = useDispatch();

    const initGlobalContextEvent = useCallback(
        ({user_id, locale = "en", user_type, device = "desktop"}: IInitPayload) => {
            if (!window.dataLayer) return;
            if (isGTMInitialized) return;

            if (user_id) dispatch(setGTMInitialized(true));

            window.dataLayer.push({
                event: "set_user_context",
                user_id,
                user_type,
                language: locale,
                device_type: device,
            });
        },
        [isGTMInitialized],
    );

    const userRegistrationIntentEvent = useCallback(() => {
        if (!window.dataLayer) return;

        window.dataLayer.push({
            event: "user_registration_intent",
        });
    }, []);

    const registrationCompleteEvent = useCallback(
        ({
            affiliate_code,
            bonus_code,
            currency,
            marketing_opt_in,
            promo_code,
            registration_method,
            signup_source,
            user_id,
            user_type,
        }: IRegistrationPayload) => {
            if (!window.dataLayer) return;

            window.dataLayer.push({
                event: "user_registration_complete",
                affiliate_code,
                bonus_code,
                currency,
                marketing_opt_in,
                promo_code,
                registration_method,
                signup_source,
                user_id,
                user_type,
            });
        },
        [],
    );

    const depositInitEvent = useCallback(
        ({amount, currency}: IDepositInitPayload) => {
            if (!window.dataLayer) return;

            window.dataLayer.push({
                event: "deposit_initiated",
                amount,
                currency,
                user_id: user?.userId,
            });
        },
        [user],
    );

    const depositSuccessEvent = useCallback(
        ({amount, currency, payment_method}: IDepositSuccessPayload) => {
            if (!window.dataLayer) return;

            window.dataLayer.push({
                event: "deposit_successful",
                amount,
                user_id: user?.userId,
                currency,
                payment_method,
            });
        },
        [user],
    );

    const depositErrorEvent = useCallback(
        ({amount, currency, message, payment_method}: IDepositErrorPayload) => {
            if (!window.dataLayer) return;

            window.dataLayer.push({
                event: "deposit_error",
                amount,
                currency,
                payment_method,
                message,
                user_id: user?.userId,
            });
        },
        [user],
    );

    const gameSessionStartEvent = useCallback(() => {
        if (!window.dataLayer) return;

        // window.dataLayer.push({
        //     event: "game_start",
        //     user_id: "",
        //     game_id: "",
        //     amount: 0,
        //     currency: "USD",
        // });
    }, []);

    const gameWagerEvent = useCallback(() => {
        if (!window.dataLayer) return;

        // window.dataLayer.push({
        //     event: "game_wager",
        //     user_id: "",
        //     game_id: "",
        //     amount: 0,
        //     currency: "USD",
        // });
    }, []);

    const gameWinEvent = useCallback(() => {
        if (!window.dataLayer) return;

        // window.dataLayer.push({
        //     event: "game_win",
        //     user_id: "",
        //     game_id: "",
        //     amount: 0,
        //     currency: "USD",
        // });
    }, []);

    const bonusClaimEvent = useCallback(
        ({bonus_code, bonus_type}: IBonusClaimPayload) => {
            if (!window.dataLayer) return;

            window.dataLayer.push({
                event: "bonus_claim",
                user_id: user?.userId,
                bonus_code,
                bonus_type,
            });
        },
        [user],
    );

    const bonusClaimSuccessEvent = useCallback(
        ({bonus_code, bonus_type, additional_parameters}: IBonusSuccessPayload) => {
            if (!window.dataLayer) return;

            window.dataLayer.push({
                event: "bonus_completed",
                user_id: user?.userId,
                bonus_code,
                bonus_type,
                additional_parameters,
            });
        },
        [user],
    );

    const modalOpenEvent = useCallback((name: string) => {
        if (!window.dataLayer) return;

        window.dataLayer.push({
            event: "modal_shown",
            modal: name,
        });
    }, []);

    const modalCloseEvent = useCallback((name: string) => {
        if (!window.dataLayer) return;

        window.dataLayer.push({
            event: "modal_closed",
            modal: name,
        });
    }, []);

    const formEvent = useCallback(({event, form_id, form_name, additional_parameters}: IFormEventPayload) => {
        if (!window.dataLayer) return;

        window.dataLayer.push({
            event,
            form_id,
            form_name,
            additional_parameters,
        });
    }, []);

    const formError = useCallback(({field, message, form_id, form_name}: IFormErrorPayload) => {
        if (!window.dataLayer) return;

        window.dataLayer.push({
            event: "form_error",
            field,
            message,
            form_id,
            form_name,
        });
    }, []);

    const event = useCallback(({currency, event, additional_parameters, user_id}: IEventPayload) => {
        if (!window.dataLayer) return;

        window.dataLayer.push({
            currency,
            event,
            additional_parameters,
            user_id,
        });
    }, []);

    return {
        isGTMInitialized,
        init: initGlobalContextEvent,
        registration: {
            intent: userRegistrationIntentEvent,
            complete: registrationCompleteEvent,
        },
        deposit: {
            init: depositInitEvent,
            success: depositSuccessEvent,
            error: depositErrorEvent,
        },
        game: {
            sessionStart: gameSessionStartEvent,
            wager: gameWagerEvent,
            win: gameWinEvent,
        },
        bonus: {
            claim: bonusClaimEvent,
            claimSuccess: bonusClaimSuccessEvent,
        },
        form: {
            event: formEvent,
            error: formError,
        },
        modal: {
            show: modalOpenEvent,
            close: modalCloseEvent,
        },
        event,
    };
};
